<script lang="ts" setup>
import { withQuery } from 'ufo'
import { storyblokResolveLinks } from 'lib/storyblokResolveLinks'
import { getMainStory } from '~/lib/content/getMainStory'
import { generateSeoLinks } from '~/lib/seo'
import { getStoryApiUrlParams } from '~/lib/storyblok'
import type { MetaSEO } from '~/types/seo'
import type { Story } from '~/types/storyblok'
import type { ArticleOverviewPageSchema } from '~/types/storyblok/article-overview-page'

/* Composables */
const storefrontStore = useStorefrontStore()
const storyblokApi = useStoryblokApi()
const { language } = useLanguage()
const { contentKey, currentStorefrontCode } = storeToRefs(storefrontStore)
const { storyblokContentVersion } = useStoryblokMode()
const route = useRoute()

const hasChildPage = computed(() => route.matched.length >= 2)

/* Data fetching */
const storyUrl = withQuery('/faq', route.query)
const { data: story, error } = await useAsyncData(storyUrl, async () => {
  const storyblokUrlParams = getStoryApiUrlParams(storyUrl, {
    contentKey: contentKey.value,
    language,
    version: storyblokContentVersion.value,
    contentType: 'article-overview-page',
    resolveLinks: 'url',
  })
  const { data } = await storyblokApi.get(`cdn/stories`, { ...storyblokUrlParams })
  let story = getMainStory(data.stories, contentKey.value, currentStorefrontCode.value)

  if (!story) {
    throw createError({
      statusCode: 404,
      message: `Storyblok data not found for ${storyUrl} and request ${withQuery(
        'https://api.storyblok.com/v2/cdn/stories',
        JSON.parse(JSON.stringify(storyblokUrlParams)),
      )}`,
    })
  }

  story = storyblokResolveLinks(story)
  return story as Story<ArticleOverviewPageSchema>
})

/* Error handling */
if (error.value)
  console.error('Storyblok data not found', error.value)
if (error.value && !story.value) {
  throw createError({
    statusCode: 404,
    message: error.value?.message || 'Storyblok data not found',
  })
}

/* Storyblok bridge */
watch(
  story,
  (newStory, oldStory) => {
    if (process.server || !newStory?.id)
      return
    if (!oldStory?.id || oldStory.id !== newStory.id)
      useStoryblokBridge(newStory.id, storyUpdate => (story.value = storyUpdate as any))
  },
  { immediate: true },
)

/* META */
const fallbackMeta: MetaSEO = { title: '', description: '' }
const { getMeta } = useMeta({
  story: null,
  storefront: storefrontStore.current,
  market: storefrontStore.currentMarket,
  SEO: story.value?.content.seo || fallbackMeta,
})
const meta = [...getMeta()]
useHead({
  title: meta[0].content,
  meta,
  link: generateSeoLinks(route),
})
</script>

<template>
  <main v-if="story" v-editable="story.content">
    <component
      :is="content.component"
      v-for="content in story.content.content"
      :key="content._uid"
      :blok="content"
    >
      <NuxtPage v-if="hasChildPage" />
    </component>
  </main>
</template>
